// apps/office/src/components/office/QuotationPDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import quotationBg from '../../pages/public/Quotation.png';

/* ── Palette ─────────────────────────────────────────────── */
const C = {
  primary:    '#4338ca', // indigo-700
  primaryLt:  '#eef2ff', // indigo-50
  accent:     '#0891b2', // cyan-600
  accentLt:   '#ecfeff',
  success:    '#059669', // emerald-600
  successLt:  '#ecfdf5',
  warn:       '#d97706', // amber-600
  warnLt:     '#fffbeb',
  ink:        '#0f172a',
  body:       '#334155',
  muted:      '#64748b',
  light:      '#f8fafc',
  border:     '#e2e8f0',
};

/* ── Styles ──────────────────────────────────────────────── */
const S = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: C.body,
    paddingBottom: 50,
  },
  bg: {
    position: 'absolute', top: 0, left: 0,
    width: 595.28, height: 841.89,
  },
  content: { paddingHorizontal: 38, paddingTop: 175 },

  /* ── Top bar (client + meta combined) ── */
  topCard: {
    flexDirection: 'row',
    backgroundColor: C.primaryLt,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 14,
    borderLeftWidth: 4,
    borderLeftColor: C.primary,
  },
  clientCol: {
    flex: 1,
    padding: 12,
    paddingRight: 8,
  },
  clientLabel: {
    fontSize: 7.5, color: C.primary, fontWeight: 'bold',
    letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5,
  },
  clientName: {
    fontSize: 13, fontWeight: 'bold', color: C.ink, marginBottom: 3,
  },
  clientLine: { fontSize: 9, color: C.body, marginBottom: 2, lineHeight: 1.4 },
  jobIdTag: {
    marginTop: 5,
    alignSelf: 'flex-start',
    backgroundColor: C.primary,
    color: '#fff',
    paddingVertical: 2, paddingHorizontal: 7,
    borderRadius: 3,
    fontSize: 8, fontWeight: 'bold',
  },

  metaCol: {
    width: 175,
    backgroundColor: '#ffffff',
    padding: 12,
    borderLeftWidth: 1,
    borderLeftColor: C.border,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
  },
  metaRowLast: { borderBottomWidth: 0 },
  metaLbl: { fontSize: 7.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 },
  metaVal: { fontSize: 9.5, color: C.ink, fontWeight: 'bold' },
  metaNum: {
    fontSize: 13, fontWeight: 'bold', color: C.primary,
    letterSpacing: 0.4,
  },

  /* ── Table ── */
  table: { marginBottom: 12 },
  tHead: {
    flexDirection: 'row',
    backgroundColor: C.primary,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  tHeadCell: { color: '#fff', fontSize: 9, fontWeight: 'bold', letterSpacing: 0.3 },
  tRow: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
    alignItems: 'flex-start',
  },
  tRowAlt: { backgroundColor: '#fbfcfe' },
  tCell: { fontSize: 9, color: C.body, lineHeight: 1.35 },
  tCellBold: { fontSize: 9.5, color: C.ink, fontWeight: 'bold', lineHeight: 1.35 },
  tCellMuted: { fontSize: 8, color: C.muted, lineHeight: 1.35, marginTop: 2 },

  cNo:     { width: 24, textAlign: 'center' },
  cDesc:   { flex: 1, paddingRight: 8 },
  cUnit:   { width: 46, textAlign: 'center' },
  cQty:    { width: 32, textAlign: 'right' },
  cRate:   { width: 62, textAlign: 'right' },
  cAmount: { width: 78, textAlign: 'right' },

  /* ── Totals ── */
  totalsWrap: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
    marginBottom: 10,
  },
  totalsBox: { width: 260 },
  totRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  totLbl: { fontSize: 9.5, color: C.muted },
  totVal: { fontSize: 9.5, color: C.ink, fontWeight: 'bold' },
  discountVal: { color: C.success },
  grandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: C.primary,
    borderRadius: 6,
    marginTop: 6,
  },
  grandLbl: { fontSize: 11, color: '#ffffff', fontWeight: 'bold', letterSpacing: 0.4 },
  grandVal: { fontSize: 12, color: '#ffffff', fontWeight: 'bold' },

  /* ── Notes ── */
  notes: {
    marginTop: 10,
    padding: 10,
    backgroundColor: C.warnLt,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: C.warn,
  },
  notesLbl: {
    fontSize: 8, color: '#92400e', fontWeight: 'bold',
    letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 4,
  },
  notesTxt: { fontSize: 9, color: '#44403c', lineHeight: 1.5 },

  /* ── Footer ── */
  footer: {
    marginTop: 22,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  thanks: {
    fontSize: 11, color: C.primary, fontWeight: 'bold', letterSpacing: 0.3,
  },
  subFoot: { fontSize: 7.5, color: C.muted, marginTop: 3 },
  poweredBy: { fontSize: 7.5, color: C.muted, textAlign: 'right' },
});

/* ── Helpers ─────────────────────────────────────────────── */
const fmtCurrency = (v) => {
  const n = Number(v) || 0;
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
  return `Rs. ${formatted}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const stripHtml = (s) => {
  if (!s) return '';
  return String(s)
    .replace(/<br\s*\/?>/gi, ' · ')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

/* ── Component ───────────────────────────────────────────── */
const QuotationPDF = ({
  quote,
  items = [],
  subtotal = 0,
  discountAmount = 0,
  taxAmount = 0,
  total = 0,
  clientJobIdText,
}) => {
  const jobIdDisplay =
    clientJobIdText ||
    quote?.clientJobIdText ||
    quote?.expand?.clientJobId?.jobId ||
    quote?.clientJobId ||
    '';

  return (
    <Document
      title={`Quotation ${quote?.quoteNumber || ''}`}
      author="HouseDesigns99"
      subject="Quotation"
    >
      <Page size="A4" style={S.page}>
        <Image src={quotationBg} style={S.bg} fixed />

        <View style={S.content}>
          {/* ───────── Top card: Client + Meta merged ───────── */}
          <View style={S.topCard}>
            {/* Left — client info */}
            <View style={S.clientCol}>
              <Text style={S.clientLabel}>Bill To</Text>
              <Text style={S.clientName}>{quote?.clientName || '—'}</Text>
              {!!quote?.clientPhone && (
                <Text style={S.clientLine}>Phone: {quote.clientPhone}</Text>
              )}
              {!!quote?.clientEmail && (
                <Text style={S.clientLine}>Email: {quote.clientEmail}</Text>
              )}
              {!!quote?.clientAddress && (
                <Text style={S.clientLine}>Address: {quote.clientAddress}</Text>
              )}
              {!!jobIdDisplay && (
                <Text style={S.jobIdTag}>Job ID: {jobIdDisplay}</Text>
              )}
            </View>

            {/* Right — quotation meta */}
            <View style={S.metaCol}>
              <View style={S.metaRow}>
                <Text style={S.metaLbl}>Quotation No.</Text>
                <Text style={S.metaNum}>{quote?.quoteNumber || '—'}</Text>
              </View>
              <View style={S.metaRow}>
                <Text style={S.metaLbl}>Date</Text>
                <Text style={S.metaVal}>{fmtDate(quote?.created)}</Text>
              </View>
              <View style={[S.metaRow, S.metaRowLast]}>
                <Text style={S.metaLbl}>Valid Until</Text>
                <Text style={S.metaVal}>{fmtDate(quote?.validUntil)}</Text>
              </View>
            </View>
          </View>

          {/* ───────── Items table ───────── */}
          <View style={S.table}>
            <View style={S.tHead} fixed>
              <Text style={[S.tHeadCell, S.cNo]}>#</Text>
              <Text style={[S.tHeadCell, S.cDesc]}>Description</Text>
              <Text style={[S.tHeadCell, S.cUnit]}>Unit</Text>
              <Text style={[S.tHeadCell, S.cQty]}>Qty</Text>
              <Text style={[S.tHeadCell, S.cRate]}>Rate</Text>
              <Text style={[S.tHeadCell, S.cAmount]}>Amount</Text>
            </View>

            {items.length === 0 ? (
              <View style={S.tRow}>
                <Text style={[S.tCell, { flex: 1, textAlign: 'center', color: C.muted }]}>
                  No items added
                </Text>
              </View>
            ) : (
              items.map((item, i) => {
                const desc = stripHtml(item.description);
                return (
                  <View
                    key={i}
                    style={[S.tRow, i % 2 === 1 && S.tRowAlt]}
                    wrap={false}
                  >
                    <Text style={[S.tCellBold, S.cNo, { color: C.primary }]}>{i + 1}</Text>
                    <View style={S.cDesc}>
                      <Text style={S.tCellBold}>{item.name || '—'}</Text>
                      {!!desc && <Text style={S.tCellMuted}>{desc}</Text>}
                    </View>
                    <Text style={[S.tCell, S.cUnit]}>{item.unit || 'Nos'}</Text>
                    <Text style={[S.tCell, S.cQty]}>{item.quantity ?? 0}</Text>
                    <Text style={[S.tCell, S.cRate]}>{fmtCurrency(item.rate)}</Text>
                    <Text style={[S.tCellBold, S.cAmount, { color: C.accent }]}>
                      {fmtCurrency(item.amount)}
                    </Text>
                  </View>
                );
              })
            )}
          </View>

          {/* ───────── Totals ───────── */}
          <View style={S.totalsWrap}>
            <View style={S.totalsBox}>
              <View style={S.totRow}>
                <Text style={S.totLbl}>Subtotal</Text>
                <Text style={S.totVal}>{fmtCurrency(subtotal)}</Text>
              </View>

              {discountAmount > 0 && (
                <View style={S.totRow}>
                  <Text style={[S.totLbl, { color: C.success }]}>
                    Discount
                    {quote?.discountType === 'percentage'
                      ? ` (${quote.discountValue || 0}%)`
                      : ''}
                  </Text>
                  <Text style={[S.totVal, S.discountVal]}>
                    − {fmtCurrency(discountAmount)}
                  </Text>
                </View>
              )}

              {taxAmount > 0 && (
                <View style={S.totRow}>
                  <Text style={S.totLbl}>GST ({quote?.taxRate || 18}%)</Text>
                  <Text style={S.totVal}>+ {fmtCurrency(taxAmount)}</Text>
                </View>
              )}

              <View style={S.grandRow}>
                <Text style={S.grandLbl}>GRAND TOTAL</Text>
                <Text style={S.grandVal}>{fmtCurrency(total)}</Text>
              </View>
            </View>
          </View>

          {/* ───────── Notes ───────── */}
          {!!quote?.notes && (
            <View style={S.notes}>
              <Text style={S.notesLbl}>Notes / Terms &amp; Conditions</Text>
              <Text style={S.notesTxt}>{stripHtml(quote.notes)}</Text>
            </View>
          )}

          {/* ───────── Footer ───────── */}
          <View style={S.footer}>
            <View>
              <Text style={S.thanks}>Thank you for your business!</Text>
              <Text style={S.subFoot}>
                This is a computer-generated quotation and does not require a signature.
              </Text>
            </View>
            <Text style={S.poweredBy}>
              Generated by{'\n'}HouseDesigns99
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default QuotationPDF;
